import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Html } from '@react-three/drei'
import { Play, Pause, RotateCcw } from 'lucide-react'
import * as THREE from 'three'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Rơi tự do (Lớp 10 — Bài 6): vật rơi từ độ cao H, a = g = 9,8 m/s².
// d = ½g·t², v = g·t. Mô hình 3D (vật + nền đất + bầu trời) bên trái; 3 đồ thị bên phải.
const G = 9.8, SCALE = 0.17, RM = 0.2
const GROUND_M = RM / SCALE        // độ cao (m) của TÂM vật khi đáy vừa chạm đất
const GW = 380, GH = 360

function FallScene({ tRef, runRef, onLand, H, tend, onGrab, rotate }: { tRef: React.MutableRefObject<number>; runRef: React.MutableRefObject<boolean>; onLand: () => void; H: number; tend: number; onGrab: () => void; rotate: boolean }) {
  const ball = useRef<THREE.Mesh>(null)
  useFrame((_, dt) => {
    if (runRef.current && tRef.current < tend) { tRef.current = Math.min(tend, tRef.current + Math.min(0.05, dt)); if (tRef.current >= tend) onLand() }
    const fallen = 0.5 * G * tRef.current * tRef.current
    // Vật dừng khi ĐÁY chạm đất: tâm ở độ cao tối thiểu = RM (đơn vị scene) → đáy = 0
    if (ball.current) ball.current.position.y = Math.max(RM, (H - fallen) * SCALE)
  })
  const poleH = H * SCALE
  return (
    <>
      <ambientLight intensity={0.8} />
      <directionalLight position={[5, 8, 6]} intensity={0.9} castShadow />
      {/* nền đất */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[14, 14]} /><meshStandardMaterial color="#86c06c" />
      </mesh>
      <gridHelper args={[14, 14, 0x6f9f57, 0x9ccf82]} position={[0, 0.01, 0]} />
      {/* cột mốc độ cao */}
      <mesh position={[-1.4, poleH / 2, 0]}><boxGeometry args={[0.08, poleH, 0.08]} /><meshStandardMaterial color="#cbd5e1" /></mesh>
      <Html position={[-1.4, poleH + 0.25, 0]} center><span style={{ color: '#475569', fontWeight: 700, fontSize: 11 }}>{Math.round(H)} m</span></Html>
      <Html position={[-1.4, 0.1, 0]} center><span style={{ color: '#475569', fontWeight: 700, fontSize: 11 }}>0</span></Html>
      {/* vật rơi */}
      <mesh ref={ball} position={[0, H * SCALE, 0]} castShadow
        onPointerDown={(e) => { e.stopPropagation(); onGrab() }}>
        <sphereGeometry args={[RM, 32, 32]} /><meshStandardMaterial color="#2563eb" metalness={0.3} roughness={0.4} />
      </mesh>
      <OrbitControls enablePan={false} enableRotate={rotate} minDistance={3} maxDistance={9} />
    </>
  )
}

const FreeFall3DSimulation: React.FC<Props> = ({
  title = 'Rơi tự do (3D)',
  description = 'Vật rơi tự do từ độ cao H với gia tốc g = 9,8 m/s². Quan sát mô hình 3D bên trái và đồ thị d, v, a bên phải. Bật/tắt từng đồ thị.',
  onComplete,
}) => {
  const gcv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const tRef = useRef(0)
  const [running, setRunning] = useState(false)
  const [H, setH] = useState(0)                  // độ cao hiện tại (m) — vật ban đầu trên mặt đất, kéo lên
  const [dragging, setDragging] = useState(false)
  const dragRef = useRef(false)
  const TEND = Math.sqrt((2 * Math.max(0.001, H - GROUND_M)) / G)   // tới khi ĐÁY vật chạm đất
  const [show, setShow] = useState({ d: true, v: true, a: true })
  const runRef = useRef(running), showRef = useRef(show)
  const firstRef = useRef(false)
  useEffect(() => { runRef.current = running }, [running])
  useEffect(() => { showRef.current = show }, [show])
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  // ── Kéo vật lên độ cao bất kì rồi thả tay → rơi ──
  const hRef = useRef(0)
  useEffect(() => { hRef.current = H }, [H])
  const onGrab = () => { if (running) return; dragRef.current = true; setDragging(true); touch() }
  const onDrag = (e: React.PointerEvent) => { if (!dragRef.current) return; const nh = Math.max(0, Math.min(30, hRef.current - e.movementY * 0.06)); hRef.current = nh; setH(nh); tRef.current = 0 }
  const onRelease = () => { if (dragRef.current) { dragRef.current = false; setDragging(false); if (hRef.current > 0.5) setRunning(true) } }

  const dis = (t: number) => 0.5 * G * t * t
  const vel = (t: number) => G * t

  const drawGraphs = useCallback(() => {
    const ctx = prepCanvas(gcv.current, GW, GH); if (!ctx) return
    ctx.clearRect(0, 0, GW, GH)
    const t = tRef.current
    const bands: { label: string; color: string; f: (t: number) => number; vmax: number; unit: string }[] = []
    const s = showRef.current
    if (s.d) bands.push({ label: 'd', color: '#2563eb', f: dis, vmax: dis(TEND), unit: 'm' })
    if (s.v) bands.push({ label: 'v', color: '#16a34a', f: vel, vmax: vel(TEND), unit: 'm/s' })
    if (s.a) bands.push({ label: 'a', color: '#dc2626', f: () => G, vmax: G * 1.4, unit: 'm/s²' })
    if (!bands.length) { ctx.fillStyle = '#94a3b8'; ctx.font = '13px sans-serif'; ctx.fillText('Bật ít nhất một đồ thị', 16, GH / 2); return }
    const bandH = GH / bands.length
    bands.forEach((bnd, bi) => {
      const top0 = bi * bandH + 12, bot = (bi + 1) * bandH - 18, x0 = 46, x1 = GW - 12
      const X = (tt: number) => x0 + (tt / TEND) * (x1 - x0)
      const Y = (val: number) => bot - (val / bnd.vmax) * (bot - top0)
      // ô lưới
      ctx.strokeStyle = '#f1f5f9'; ctx.lineWidth = 1
      for (let i = 1; i <= 4; i++) { const gx = x0 + (i / 4) * (x1 - x0); ctx.beginPath(); ctx.moveTo(gx, top0 - 4); ctx.lineTo(gx, bot); ctx.stroke() }
      for (let gi = 1; gi <= 2; gi++) { const gy = bot - (gi / 2) * (bot - top0); ctx.beginPath(); ctx.moveTo(x0, gy); ctx.lineTo(x1, gy); ctx.stroke() }
      // trục
      ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 1.8; ctx.lineJoin = 'round'
      ctx.beginPath(); ctx.moveTo(x0, top0 - 4); ctx.lineTo(x0, bot); ctx.lineTo(x1, bot); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(x0, top0 - 9); ctx.lineTo(x0 - 4, top0 - 1); ctx.lineTo(x0 + 4, top0 - 1); ctx.closePath(); ctx.fill()
      ctx.beginPath(); ctx.moveTo(x1 + 5, bot); ctx.lineTo(x1 - 3, bot - 4); ctx.lineTo(x1 - 3, bot + 4); ctx.closePath(); ctx.fill()
      ctx.font = 'bold 12px sans-serif'; ctx.fillStyle = bnd.color; ctx.fillText(`${bnd.label} (${bnd.unit})`, x0 + 2, top0 + 2)
      ctx.fillStyle = '#94a3b8'; ctx.font = '10px sans-serif'; ctx.fillText('t (s)', x1 - 20, bot + 13)
      // đường cong tới t
      ctx.strokeStyle = bnd.color; ctx.lineWidth = 2.5; ctx.beginPath()
      for (let i = 0; i <= 60; i++) { const tt = (t * i) / 60; const px = X(tt), py = Y(bnd.f(tt)); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) }
      ctx.stroke()
      ctx.fillStyle = bnd.color; ctx.beginPath(); ctx.arc(X(t), Y(bnd.f(t)), 4, 0, Math.PI * 2); ctx.fill()
    })
  }, [TEND])

  const tick = useCallback(() => { drawGraphs(); raf.current = requestAnimationFrame(tick) }, [drawGraphs])
  useEffect(() => { raf.current = requestAnimationFrame(tick); return () => { if (raf.current) cancelAnimationFrame(raf.current) } }, [tick])

  const Chk = ({ k, label, color }: { k: 'd' | 'v' | 'a'; label: string; color: string }) => (
    <label className="flex items-center gap-1.5 cursor-pointer select-none"><input type="checkbox" checked={show[k]} onChange={(e) => { touch(); setShow((s) => ({ ...s, [k]: e.target.checked })) }} className="w-4 h-4" style={{ accentColor: color }} /><span style={{ color }} className="font-bold">{label}</span></label>
  )

  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="flex flex-col md:flex-row gap-3">
        <div className={`md:flex-1 rounded-lg overflow-hidden border border-gray-100 ${dragging ? 'cursor-ns-resize' : ''}`} style={{ height: 360, background: 'linear-gradient(#cfeaff,#eef7ff)' }}
          onPointerMove={onDrag} onPointerUp={onRelease} onPointerLeave={onRelease}>
          <Canvas camera={{ position: [3.5, 2.2, 5], fov: 50 }} shadows onCreated={touch}>
            <FallScene tRef={tRef} runRef={runRef} onLand={() => setRunning(false)} H={H} tend={TEND} onGrab={onGrab} rotate={!dragging} />
          </Canvas>
        </div>
        <div className="md:flex-1"><canvas ref={gcv} width={GW} height={GH} className="w-full rounded-lg border border-gray-100 bg-slate-50" style={{ height: 360 }} /></div>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm">
        <span className="text-gray-600">Hiện đồ thị:</span>
        <Chk k="d" label="d (độ cao rơi)" color="#2563eb" />
        <Chk k="v" label="v (vận tốc)" color="#16a34a" />
        <Chk k="a" label="a = g" color="#dc2626" />
      </div>
      <label className="flex items-center gap-2 text-sm mt-3"><span className="w-28 text-gray-600">Độ cao thả H</span>
        <input type="range" min={0} max={30} step={1} value={H} disabled={running} onChange={(e) => { touch(); setH(Number(e.target.value)); tRef.current = 0; setRunning(false) }} className="flex-1 disabled:opacity-40" />
        <span className="w-14 text-right font-semibold">{Math.round(H)} m</span></label>
      <div className="flex gap-2 mt-3">
        <button onClick={() => { touch(); if (tRef.current >= TEND) tRef.current = 0; setRunning((r) => !r) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700">{running ? <><Pause className="w-4 h-4" /> Dừng</> : <><Play className="w-4 h-4" /> Thả</>}</button>
        <button onClick={() => { touch(); tRef.current = 0; setRunning(false); setH(0) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"><RotateCcw className="w-4 h-4" /> Về mặt đất</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2"><b>Kéo quả cầu lên</b> độ cao bất kì (hoặc dùng thanh trượt) rồi <b>buông tay</b> → vật rơi tự do. a = g không đổi → v tăng đều (đường thẳng), d = ½g·t² (parabol). Kéo chuột vùng trống để xoay mô hình 3D.</p>
    </div>
  )
}
export default FreeFall3DSimulation
